import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceService } from '../services/deviceService';
import {
    TDevice,
    TDeviceEvents,
    TDeviceMessages,
    TConnectionDetails,
    TCreateDeviceProp,
    TDevicesList,
    TDeviceModel
} from '../context/DeviceProvider';
import { handleApiError } from '../lib/errorHandler';

interface DeviceListOptions {
    search?: string;
    manufacturers?: string[];
    models?: string[];
    statuses?: string[];
}

export function useDeviceOperations() {
    const [deviceDetail, setDeviceDetail] = useState<TDevice | null>(null);
    const [deviceEvents, setDeviceEvents] = useState<TDeviceEvents[]>([]);
    const [deviceMessages, setDeviceMessages] = useState<TDeviceMessages[]>([]);
    const [connectionDetails, setConnectionDetails] = useState({} as TConnectionDetails);
    const [devicesList, setDevicesList] = useState<TDevicesList>({
        count: 0,
        results: [],
    });
    const [deviceModels, setDeviceModels] = useState<TDeviceModel[]>([]);
    const [deviceManufacturers, setDeviceManufacturers] = useState([]);
    const { t } = useTranslation();
    const [deviceLoading, setDevIceLoading] = useState(false)
    const getDeviceList = async (
        options: DeviceListOptions,
        limit: number,
        offset: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            setDevIceLoading(true)
            const response = await deviceService.getDevices({
                limit,
                offset,
                search: options.search,
                manufacturers: options.manufacturers,
                models: options.models,
                statuses: options.statuses,
            });

            if (response.data) {
                setDevicesList(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingList'), "devices");
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }finally{
            setDevIceLoading(false)
        }
    };

    const getDeviceDetail = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.getDeviceDetail(id);
            if (response.data) {
                setDeviceDetail(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingDetail'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const createDevice = async (
        data: TCreateDeviceProp,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.createDevice(data);
            if (response.data) {
                window.location.assign('/devices');
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.creating'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const editDevice = async (
        id: number,
        data: TCreateDeviceProp,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.editDevice(id, data);
            if (response.data) {
                window.location.assign('/devices');
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.editing'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const pingDevice = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.pingDevice(id);
            if (response.data) {
                setAlertMessages(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.pinging'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const deactivateDevice = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            await deviceService.deactivateDevice(id);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.deactivating'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const activateDevice = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            await deviceService.activateDevice(id);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.activating'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const deleteDevice = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            await deviceService.deleteDevice(id);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.deleting'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const getDeviceLatestEvents = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.getDeviceEvents(id);
            if (response.data) {
                setDeviceEvents(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingEvents'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const getDeviceLatestMessages = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.getDeviceMessages(id);
            if (response.data) {
                setDeviceMessages(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingMessages'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const GetAllDeviceMessages = async (
        id: number,
        offset: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.GetAllDeviceMessages(id, offset);
            if (response.data) {
                return response.data
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingMessages'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const getDeviceConnection = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.getDeviceConnectionSettings(id);
            if (response.data) {
                setConnectionDetails(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingConnection'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updateConnectionSettings = async (
        id: number,
        params: TConnectionDetails,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ): Promise<TConnectionDetails | null> => {
        try {
            const response = await deviceService.updateConnection(id, params);
            if (response.data) {
                setConnectionDetails(response.data);
                setAlertMessages({});
                return response.data;
            }
            return null;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.updatingConnection'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
            return null;
        }
    };

    const getDeviceModels = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.getDeviceModels();
            if (response.data) {
                setDeviceModels(response.data.results);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingModels'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const getDeviceManufacturers = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await deviceService.getDeviceManufacturers();
            if (response.data) {
                setDeviceManufacturers(response.data.results);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.devices.loadingManufacturers'), 'devices');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    return {
        deviceDetail,
        deviceEvents,
        deviceMessages,
        connectionDetails,
        devicesList,
        deviceModels,
        deviceManufacturers,
        deviceLoading,
        setDeviceDetail,
        getDeviceList,
        getDeviceDetail,
        createDevice,
        editDevice,
        pingDevice,
        deactivateDevice,
        activateDevice,
        deleteDevice,
        getDeviceLatestEvents,
        getDeviceLatestMessages,
        GetAllDeviceMessages,
        getDeviceConnection,
        updateConnectionSettings,
        getDeviceModels,
        getDeviceManufacturers,
    };
}
